import { useEffect, useMemo, useRef, useState } from 'react'
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Line, Tooltip, Legend, BarChart, Bar, PieChart, Pie, Cell } from 'recharts'
import { getChartPalette, getModelColorMap } from '../../chartPalette'

const PALETTE = getChartPalette()
const FIXED_COLORS = getModelColorMap()

const MAX_BAR_SIZE = 90

function norm(s) {
  return String(s || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase()
}
function nextUnusedFrom(startIdx, usedSet) {
  for (let i = 0; i < PALETTE.length; i++) {
    const idx = (startIdx + i) % PALETTE.length
    const c = PALETTE[idx]
    if (!usedSet.has(c)) return c
  }
  return PALETTE[startIdx % PALETTE.length]
}

function buildColorMap(modelKeys) {
  const originals = {}
  const normalizedModels = modelKeys.map((m) => {
    const n = norm(m)
    originals[n] = m
    return n
  })

  const present = new Set(normalizedModels)
  const colorByNorm = {}
  const used = new Set()

  Object.keys(FIXED_COLORS).forEach((key) => {
    const n = norm(key)
    if (present.has(n)) {
      const c = FIXED_COLORS[key]
      colorByNorm[n] = c
      used.add(c)
    }
  })

  normalizedModels.forEach((m, i) => {
    if (!colorByNorm[m]) {
      const preferred = PALETTE[i % PALETTE.length]
      const assigned = used.has(preferred) ? nextUnusedFrom(i, used) : preferred
      colorByNorm[m] = assigned
      used.add(assigned)
    }
  })

  const colorMap = {}
  normalizedModels.forEach((n) => {
    colorMap[originals[n]] = colorByNorm[n]
  })
  return colorMap
}

const CustomLegend = ({ payload }) => {
  if (!payload || payload.length === 0) return null

  return (
    <div className="custom-legend-container">
      {payload.map((entry) => (
        <div className="legend-item" key={entry.value}>
          <span className="legend-color-dot" style={{ background: entry.color }} />
          <span className="legend-label">{entry.value}</span>
        </div>
      ))}
    </div>
  )
}

const RegisteredDevicesChart = ({ data = [], chartType = 'line' }) => {
  const containerRef = useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  const chartData = Array.isArray(data) && data.length ? data : [{ date: '' }]
  const modelKeys = useMemo(() => {
    const keys = Array.from(new Set(chartData.flatMap((row) => Object.keys(row).filter((k) => k !== 'date'))))
    return keys.sort((a, b) => a.localeCompare(b))
  }, [chartData])
  const colorMap = buildColorMap(modelKeys)

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return (
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend content={CustomLegend} />
            {modelKeys.map((model, idx) => (
              <Bar
                key={model}
                maxBarSize={MAX_BAR_SIZE}
                dataKey={model}
                fill={colorMap[model]}
                name={model}
                activeShape={(props) => <rect {...props} fill="transparent" stroke="none" />}
              />
            ))}
          </BarChart>
        )
      case 'pie':
        const pieData = modelKeys.map((model) => ({
          name: model,
          value: chartData.reduce((sum, row) => sum + Number(row[model] || 0), 0),
        }))
        return (
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="55%" outerRadius={250} label isAnimationActive={false}>
              {pieData.map((entry, idx) => (
                <Cell key={entry.name} fill={colorMap[entry.name]} />
              ))}
            </Pie>
            <Legend content={CustomLegend} />
            <Tooltip />
          </PieChart>
        )
      case 'stackedBar':
        return (
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend content={CustomLegend} />
            {modelKeys.map((model, idx) => (
              <Bar key={model} dataKey={model} maxBarSize={MAX_BAR_SIZE} fill={colorMap[model]} name={model} stackId="total" />
            ))}
          </BarChart>
        )
      case 'line':
      default:
        return (
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend content={CustomLegend} />
            {modelKeys.map((model, idx) => (
              <Line
                key={model}
                type="monotone"
                dataKey={model}
                stroke={colorMap[model]}
                strokeWidth={2}
                activeDot={{ r: 5 }}
                dot={false}
                name={model}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        )
    }
  }

  useEffect(() => {
    const observer = new window.ResizeObserver((entries) => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect
        setDimensions({ width, height })
      }
    })
    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={containerRef} style={{ width: '100%', height: 650 }} className="chart-responsive-wrapper">
      <div className="chart-min-width">
        {dimensions.width > 0 && dimensions.height > 0 && (
          <ResponsiveContainer width="100%" height="100%" key={`${dimensions.width}x${dimensions.height}`}>
            {renderChart()}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

export default RegisteredDevicesChart

